import { pick } from 'underscore';
import {
  WaveformFidelity,
  WaveformPlacement,
} from 'components/VideoTemplateEditor/types';
import { Soundwave, SoundwaveType } from 'types';
import { defaultSoundwaveState } from 'utils/embed/soundwave';
import measurement from 'utils/measurement';
import { createNewLayer, deleteLayer } from '../../shared/layers';
import { updateState } from '../../shared/mutations';
import { SoundwaveState, VideoTemplateStateContent } from '../../types';
import {
  getReplacementSoundwaveDimensions,
  soundwavePlacementToDimensions,
} from './utils';

export function createSoundwaveRestorePoint(state: VideoTemplateStateContent) {
  const { soundwave } = state;
  return {
    ...state,
    soundwave: {
      ...soundwave,
      original: pick(
        soundwave,
        'type',
        'color',
        'height',
        'left',
        'top',
        'width',
      ),
    },
  };
}

export function setSoundwaveColor(
  state: VideoTemplateStateContent,
  color: string,
) {
  return updateState(state, {
    soundwave: {
      color,
      waveformPrefId: null,
    },
  });
}

function updateSoundwaveLayer(
  oldState: VideoTemplateStateContent,
  newState: VideoTemplateStateContent,
): VideoTemplateStateContent {
  const oldType = oldState.soundwave?.type ?? 'none';
  const newType = newState.soundwave?.type ?? 'none';

  if (oldType !== 'none' && newType === 'none') {
    const { layers } = newState;
    const layerId = layers.order.find(
      id => layers.data[id].type === 'waveform',
    );
    return {
      ...updateState(newState, {
        soundwave: {
          ...newState.soundwave,
          layerId: undefined,
        },
      }),
      ...deleteLayer(newState, layerId),
    };
  }

  if (oldType === 'none' && newType !== 'none') {
    const [layerId, layerState] = createNewLayer(newState, 'waveform');
    return updateState(newState, {
      soundwave: {
        ...newState.soundwave,
        layerId,
      },
      layers: layerState,
    });
  }

  return newState;
}

export function setSoundwaveType(
  state: VideoTemplateStateContent,
  newType: SoundwaveType,
) {
  const newState = updateState(state, {
    soundwave: {
      ...getReplacementSoundwaveDimensions(state, newType),
      type: newType,
      color: state?.soundwave?.color ?? defaultSoundwaveState.get('waveColor'),
      waveformPrefId: null,
    },
  });

  return updateSoundwaveLayer(state, newState);
}

export function setSoundwaveDimensions(
  state: VideoTemplateStateContent,
  dims: Pick<SoundwaveState, 'height' | 'left' | 'top' | 'width'>,
) {
  return updateState(state, {
    soundwave: { ...dims, waveformPrefId: null },
  });
}

export function setSoundwavePlacement(
  state: VideoTemplateStateContent,
  placement: WaveformPlacement,
) {
  return updateState(state, {
    soundwave: {
      waveformPrefId: null,
      ...soundwavePlacementToDimensions(
        state.soundwave,
        state.aspectRatio,
        placement,
      ),
    },
  });
}

// helper function to set the soundwave state from the shape used elsewhere
// in the app
// TODO this probably belongs somewhere else.  might want to make the distinction
// between which of the state utils are used internally and which are intended
// to be used externally.  as implementation progresses, the functions which
// need to be available externally should become clear
//
export function setSoundwave(
  state: VideoTemplateStateContent,
  soundwave: Soundwave,
) {
  if (!soundwave) return state;
  const newState = updateState(state, {
    soundwave: {
      color: soundwave.waveColor,
      fidelity: soundwave.waveGeneration === 'accurate' ? 'hi-fi' : 'lo-fi',
      height: measurement(soundwave.waveSize.height),
      left: measurement(soundwave.wavePosition.left, 'vw'),
      top: measurement(soundwave.wavePosition.top, 'vh'),
      type: soundwave.waveType,
      width: measurement(soundwave.waveSize.width),
      waveformPrefId: soundwave.waveformPrefId,
    },
  });

  return updateSoundwaveLayer(state, newState);
}

export function resetSoundwave(state: VideoTemplateStateContent) {
  const newState = updateState(state, {
    soundwave: {
      ...state.soundwave.original,
    },
  });
  return updateSoundwaveLayer(state, newState);
}

export function setWaveformFidelity(
  state: VideoTemplateStateContent,
  fidelity: WaveformFidelity,
) {
  return {
    ...state,
    soundwave: {
      ...state.soundwave,
      waveformPrefId: null,
      fidelity,
    },
  };
}
